'use client';
/** @jsxImportSource @emotion/react */

import { type ElementType, forwardRef, type Ref } from 'react';

import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import MuiChip, {
  type ChipTypeMap as MuiChipTypeMap,
  type ChipProps as MuiChipProps,
} from '@mui/material/Chip';

type ExcludedMuiProps = 'avatar';

type CommonProps = {
  component?: ElementType;
};

type ChipMedium = CommonProps & {
  avatar?: MuiChipProps['avatar'];
  size?: 'medium';
};

type ChipSmall = CommonProps & {
  avatar?: never;
  size?: 'small';
};

type CustomProps = ChipMedium | ChipSmall;

type ChipTypeMap<
  P = CustomProps,
  D extends ElementType = MuiChipTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiChipTypeMap['props'], ExcludedMuiProps> & P;
};

export type ChipProps<
  D extends ElementType = MuiChipTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<MuiChipTypeMap<P, D>, D>;

const ChipComponent = (
  { size = 'medium', sx, ...props }: ChipProps,
  ref: Ref<HTMLDivElement>
) => {
  const sxStyles = [
    ...(Array.isArray(sx) ? sx : [sx]),
    {
      '& .MuiChip-label': {
        // ZDS-992 to fix the chip text position on Windows
        transform: 'translateY(-1px)',
      },
    },
  ];
  return <MuiChip ref={ref} size={size} sx={sxStyles} {...props} />;
};

ChipComponent.displayName = 'Chip';

export const Chip = forwardRef(
  ChipComponent
) as OverridableComponent<ChipTypeMap>;
